import { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react'
import { Button } from '@progress/kendo-react-buttons'
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs'
import { Input } from '@progress/kendo-react-inputs'
import { WorkingSection, MaterialWorking, LabourWorking } from '../../data/interfaces'
import {
  getWorkingSectionsByEnquiry,
  getWorkingSectionsByQuote,
  getWorkingSectionsByJob,
  createWorkingSection,
  updateWorkingSection,
  deleteWorkingSection
} from '../../data/sectionsData'
import { useToast } from '../../contexts/ToastContext'
import { FaChevronDown, FaChevronRight, FaEdit, FaTrash, FaPlus } from 'react-icons/fa'
import MaterialWorkingsTable from './MaterialWorkingsTable'
import LabourWorkingsTable from './LabourWorkingsTable'
import { materialWorkingTotal, labourWorkingTotal, inSection } from './workingTotals'
import './WorkingSections.css'

interface WorkingSectionsViewProps {
  enquiryId?: number
  quoteId?: number
  jobId?: number
  lockEdit: boolean
  materialWorkings: MaterialWorking[]
  onMaterialWorkingsChange: (workings: MaterialWorking[]) => void
  labourWorkings: LabourWorking[]
  onLabourWorkingsChange: (workings: LabourWorking[]) => void
  // When the parent renders its own "Add Section" control (e.g. in a section
  // header) it can hide the built-in toolbar and drive adding via the ref.
  hideToolbar?: boolean
}

export interface WorkingSectionsViewHandle {
  openAddSection: () => void
  canAddSection: boolean
}

const WorkingSectionsView = forwardRef<WorkingSectionsViewHandle, WorkingSectionsViewProps>(({
  enquiryId,
  quoteId,
  jobId,
  lockEdit,
  materialWorkings,
  onMaterialWorkingsChange,
  labourWorkings,
  onLabourWorkingsChange,
  hideToolbar
}, ref) => {
  const { showToast } = useToast()
  const [sections, setSections] = useState<WorkingSection[]>([])
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({})
  const [generalCollapsed, setGeneralCollapsed] = useState(false)
  const [sectionDialogVisible, setSectionDialogVisible] = useState(false)
  const [editingSection, setEditingSection] = useState<WorkingSection | null>(null)
  const [sectionName, setSectionName] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const entityId = jobId ?? quoteId ?? enquiryId

  const fetchSections = useCallback(async () => {
    try {
      let data: WorkingSection[] = []
      if (jobId) {
        data = await getWorkingSectionsByJob(jobId)
      } else if (quoteId) {
        data = await getWorkingSectionsByQuote(quoteId)
      } else if (enquiryId) {
        data = await getWorkingSectionsByEnquiry(enquiryId)
      }
      setSections(data)
    } catch (error) {
      console.error('Error fetching working sections:', error)
    }
  }, [jobId, quoteId, enquiryId])

  useEffect(() => {
    fetchSections()
  }, [fetchSections])

  const toggleCollapsed = (sectionId: number) => {
    setCollapsed(prev => ({ ...prev, [sectionId]: !prev[sectionId] }))
  }

  const openAddSection = () => {
    setEditingSection(null)
    setSectionName('')
    setSectionDialogVisible(true)
  }

  useImperativeHandle(ref, () => ({
    openAddSection,
    canAddSection: !isLoading && !!entityId,
  }), [isLoading, entityId])

  const openEditSection = (section: WorkingSection) => {
    setEditingSection(section)
    setSectionName(section.name || '')
    setSectionDialogVisible(true)
  }

  const handleSaveSection = async () => {
    if (sectionName.trim() === '') {
      showToast('Please enter a section name', 'error')
      return
    }
    setIsLoading(true)
    try {
      if (editingSection?.workingSectionId) {
        const updated = await updateWorkingSection(editingSection.workingSectionId, {
          ...editingSection,
          name: sectionName.trim()
        })
        setSections(prev => prev.map(s => s.workingSectionId === editingSection.workingSectionId ? updated : s))
        showToast('Section updated successfully', 'success')
      } else {
        const created = await createWorkingSection({
          enquiryId,
          quoteId,
          jobId,
          name: sectionName.trim(),
          sortOrder: sections.length
        })
        setSections(prev => [...prev, created])
        showToast('Section created successfully', 'success')
      }
      setSectionDialogVisible(false)
      setEditingSection(null)
      setSectionName('')
    } catch (error) {
      console.error('Error saving section:', error)
      showToast('Error saving section', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteSection = async (section: WorkingSection) => {
    if (!section.workingSectionId) return
    if (!window.confirm(`Delete the "${section.name}" section? Its workings will be moved to General.`)) return

    setIsLoading(true)
    try {
      const deletedId = section.workingSectionId
      await deleteWorkingSection(deletedId)
      setSections(prev => prev.filter(s => s.workingSectionId !== deletedId))
      // The backend clears section_id on affected workings; mirror that locally
      onMaterialWorkingsChange(materialWorkings.map(w => w.sectionId === deletedId ? { ...w, sectionId: undefined } : w))
      onLabourWorkingsChange(labourWorkings.map(w => w.sectionId === deletedId ? { ...w, sectionId: undefined } : w))
      showToast('Section deleted successfully', 'success')
    } catch (error) {
      console.error('Error deleting section:', error)
      showToast('Error deleting section', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  // Legacy workings — recorded before sections existed, so they carry no
  // section id. Their presence is what keeps the General card on show.
  const hasUnsectionedWorkings =
    materialWorkings.some(w => w.sectionId == null) ||
    labourWorkings.some(w => w.sectionId == null)

  const materialSubtotal = (sectionId?: number) =>
    materialWorkings.filter(w => inSection(w.sectionId, sectionId)).reduce((sum, w) => sum + materialWorkingTotal(w), 0)

  const labourSubtotal = (sectionId?: number) =>
    labourWorkings.filter(w => inSection(w.sectionId, sectionId)).reduce((sum, w) => sum + labourWorkingTotal(w), 0)

  const renderCard = (
    key: string,
    title: string,
    sectionId: number | undefined,
    section?: WorkingSection
  ) => {
    const isCollapsed = sectionId != null ? !!collapsed[sectionId] : generalCollapsed
    const mSub = materialSubtotal(sectionId)
    const lSub = labourSubtotal(sectionId)
    const sectionTotal = mSub + lSub

    return (
      <div className="working-section-card" key={key}>
        <div className="working-section-header">
          <button
            type="button"
            className="working-section-toggle"
            onClick={() => sectionId != null ? toggleCollapsed(sectionId) : setGeneralCollapsed(prev => !prev)}
          >
            {isCollapsed ? <FaChevronRight size={12} /> : <FaChevronDown size={12} />}
            <span className="working-section-title">{title}</span>
          </button>
          <div className="working-section-header-right">
            {section && !lockEdit && (
              <>
                <Button size="small" fillMode="flat" onClick={() => openEditSection(section)} disabled={isLoading} title="Rename section">
                  <FaEdit />
                </Button>
                <Button size="small" fillMode="flat" themeColor="error" onClick={() => handleDeleteSection(section)} disabled={isLoading} title="Delete section">
                  <FaTrash />
                </Button>
              </>
            )}
          </div>
        </div>

        {!isCollapsed && (
          <div className="working-section-body">
            <div className="working-type-heading">
              <span>Material Workings</span>
            </div>
            <MaterialWorkingsTable
              materialWorkings={materialWorkings}
              enquiryId={enquiryId}
              quoteId={quoteId}
              jobId={jobId}
              sectionId={sectionId}
              lockEdit={lockEdit}
              onMaterialWorkingsChange={onMaterialWorkingsChange}
            />
            {/* Under the lines rather than above them, so it reads as the sum of
                what you have just looked at. */}
            <div className="working-type-subtotal-row">
              <span className="working-type-subtotal-label">Material Total</span>
              <span className="working-type-subtotal">£{mSub.toFixed(2)}</span>
            </div>

            <div className="working-type-heading" style={{ marginTop: '1rem' }}>
              <span>Labour Workings</span>
            </div>
            <LabourWorkingsTable
              labourWorkings={labourWorkings}
              enquiryId={enquiryId}
              quoteId={quoteId}
              jobId={jobId}
              sectionId={sectionId}
              lockEdit={lockEdit}
              onLabourWorkingsChange={onLabourWorkingsChange}
            />
            <div className="working-type-subtotal-row">
              <span className="working-type-subtotal-label">Labour Total</span>
              <span className="working-type-subtotal">£{lSub.toFixed(2)}</span>
            </div>
          </div>
        )}

        <div className="working-section-footer">
          <span className="working-section-footer-label">Total</span>
          <span className="working-section-subtotal">£{sectionTotal.toFixed(2)}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="working-sections">
      {!lockEdit && !hideToolbar && (
        <div className="working-sections-toolbar">
          <Button themeColor="primary" onClick={openAddSection} disabled={isLoading || !entityId} title={!entityId ? 'Save the record before adding sections' : 'Add a new section'}>
            <FaPlus style={{ marginRight: '0.35rem' }} /> Add Section
          </Button>
        </div>
      )}

      {sections.map(section => renderCard(
        `section-${section.workingSectionId}`,
        section.name || 'Untitled section',
        section.workingSectionId,
        section
      ))}

      {/* General is virtual: it exists only to give a home to workings recorded
          before sections were introduced, which come back with no section id.
          A record with none of those never sees it, so anything new has to be
          filed into a section the user has actually created. It reappears if a
          section is deleted, since that releases its workings back here. */}
      {hasUnsectionedWorkings && renderCard('general', 'General', undefined)}

      {sectionDialogVisible && (
        <Dialog
          title={editingSection ? 'Rename Section' : 'Add Section'}
          onClose={() => { setSectionDialogVisible(false); setEditingSection(null) }}
          width={400}
        >
          <div style={{ padding: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Section name</label>
            <Input
              value={sectionName}
              onChange={(e) => setSectionName(e.value as string)}
              placeholder="e.g. Bathroom"
              style={{ width: '100%' }}
            />
          </div>
          <DialogActionsBar>
            <Button themeColor="primary" onClick={handleSaveSection} disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
            <Button onClick={() => { setSectionDialogVisible(false); setEditingSection(null) }} disabled={isLoading}>
              Cancel
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  )
})

export default WorkingSectionsView
